import { AlertAction, IAlertProps } from "@components/alert/Alert.types";
import { IWithAlertSettings } from "@components/alert/withAlert";
import { IValueInterval } from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { getDrillDownLink, getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { formatDateToDateString } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { IFieldDef } from "@components/smart/FieldInfo";
import { IColumn, IRow } from "@components/table";
import BindingContext, { createPath } from "@odata/BindingContext";
import { getEntitySetByDocumentType, getRouteByDocumentType } from "@odata/EntityTypes";
import {
    DocumentEntity,
    EntityTypeName,
    IDocumentEntity,
    IFiscalYearCloseEntity,
    IFiscalYearEntity,
    IInternalDocumentEntity,
    InternalDocumentEntity,
    IOtherLiabilityEntity,
    IOtherReceivableEntity,
    OtherLiabilityEntity,
} from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode, FiscalYearCloseSectionTypeCode, LegalEntityCategoryCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { OData } from "@odata/OData";
import { CUSTOM_DATE_RANGE_ID } from "@pages/reports/customFilterComponents/ComposedDateRange.utils";
import {
    accruedExpenseFilter,
    accruedRevenueFilter,
    excludeInitialBalancesFilter,
    getOpenAccrualFilter,
} from "@utils/accounting";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { arrayInsert } from "@utils/general";
import i18next, { TFunction } from "i18next";
import React, { Fragment, ReactElement } from "react";
import { FormStorage } from "views/formView/FormStorage";

import TableWithAutoSizedColumns from "../../components/table/TableWithAutoSizedColumns";
import Tabs from "../../components/tabs";
import { EMPTY_DASH, REST_API_URL } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { Status, TextAlign } from "../../enums";
import { TRecordString } from "../../global.types";
import { ROUTE_ACCOUNT_ANALYSIS, ROUTE_CHARTS_OF_ACCOUNTS } from "../../routes";
import TestIds from "../../testIds";
import { formatCurrency } from "../../types/Currency";
import DateType, { getUtcDayjs } from "../../types/Date";
import customFetch from "../../utils/customFetch";
import memoize from "../../utils/memoize";
import { AccountAnalysisProps, ClosingAccounts } from "../reports/accountAnalysis/AccountAnalysisDef";
import { CommonReportProps } from "../reports/CommonDefs";
import {
    AccountNamesTableStyled,
    AccountNameStyled,
    AdditionalAccountInfoTable,
    TableWrapper,
} from "./FiscalYearClose.styles";

const LOCAL_CONTEXT_DUE_DATE = "DUEDATE";

export const FISCAL_YEAR_PROCESSING_URL = `${REST_API_URL}/FiscalYearProcessing`;
export const STEP_VALIDATION_URL = `${FISCAL_YEAR_PROCESSING_URL}/ValidateOnClose`;
export const GET_TAX_BASE_REDUCTION = `${FISCAL_YEAR_PROCESSING_URL}/GetTaxSavings`;

export const openAccrualsRevenueFilter = `${getOpenAccrualFilter()} AND ${accruedRevenueFilter} AND ${excludeInitialBalancesFilter}`;
export const openAccrualsExpenseFilter = `${getOpenAccrualFilter()} AND ${accruedExpenseFilter} AND ${excludeInitialBalancesFilter}`;

export const getFYDateRangeDrilldownFilter = (fy: IFiscalYearEntity): IValueInterval => {
    return {
        from: formatDateToDateString(fy?.DateStart),
        to: formatDateToDateString(fy?.DateEnd),
    };
};

export interface IBaseFiscalYearCloseState {
    loading: boolean;
    switchBusy?: boolean;
    rows?: IRow[];
}

type IFYCloseGeneratedDoc = IInternalDocumentEntity | IOtherLiabilityEntity;

type RenderDocumentsTabOptions = {
    renderExtraColumns?: boolean;
    includeDueDate?: boolean;
    includeDocumentType?: boolean;
    storage?: FormStorage;
    style?: unknown;
} | undefined;

export const renderDocumentsTab = (
    docs: IFYCloseGeneratedDoc[],
    t: TFunction,
    context: IAppContext,
    isLoading: boolean,
    options?: RenderDocumentsTabOptions
): ReactElement => {
    return (
        <>
            <Tabs
                style={options.style}
                data={[
                    {
                        id: "documents",
                        title: t(`FiscalYearClose:${options?.renderExtraColumns ? "InternalDocs" : "Documents"}`),
                    },
                ]}
                selectedTabId={"documents"}
            />
            {renderDocTable(docs, t, context, isLoading, {
                ...options,
                storage: options?.storage,
            })}
        </>
    );
};

export const documentProps = (
    id: string,
    documentTypeCode = DocumentTypeCode.InternalDocument,
    useDateDue = false
): IFieldDef => {
    const def: IFieldDef = {
        id,
        additionalProperties: [
            { id: DocumentEntity.DateAccountingTransaction },
            { id: DocumentEntity.NumberOurs },
            {
                id: documentTypeCode === DocumentTypeCode.InternalDocument
                    ? InternalDocumentEntity.Explanation
                    : DocumentEntity.Note,
            },
            { id: DocumentEntity.DateCreated },
            { id: DocumentEntity.Amount },
            { id: DocumentEntity.DocumentTypeCode },
            { id: DocumentEntity.DateCbaDocument },
        ],
    };

    if (useDateDue) {
        def.additionalProperties = [...def.additionalProperties, { id: OtherLiabilityEntity.DateDue }];
    }

    return def;
};

export const renderDocTable = (
    docs: (IInternalDocumentEntity | IOtherLiabilityEntity)[],
    t: TFunction,
    context: IAppContext,
    isLoading: boolean,
    options?: RenderDocumentsTabOptions
): ReactElement => {
    let columns: IColumn[] = [
        { id: "date", label: t("FiscalYearClose:Final.Date"), textAlign: TextAlign.Left },
        { id: "doc", label: t("FiscalYearClose:Final.Doc"), textAlign: TextAlign.Left },
        { id: "name", label: t("FiscalYearClose:Final.Relation"), textAlign: TextAlign.Left },
        { id: "sum", label: t("FiscalYearClose:Final.Sum"), textAlign: TextAlign.Right },
    ];

    if (options?.includeDocumentType) {
        columns = arrayInsert(
            columns,
            {
                id: "docType",
                label: t("FiscalYearClose:Final.DocumentType"),
                textAlign: TextAlign.Left,
            },
            1
        );
    }

    if (options?.includeDueDate) {
        columns = arrayInsert(
            columns,
            {
                id: "dueDate",
                label: t("FiscalYearClose:Final.ProposedDueDate"),
                textAlign: TextAlign.Right,
            },
            1
        );
    }


    const rows = docs?.sort(sortByDate).map((doc) => {
        const localContextDueDateKey = BindingContext.localContext(
            createPath(LOCAL_CONTEXT_DUE_DATE, doc?.Id?.toString())
        );
        const isValidPath = options?.storage?.isValidPath(localContextDueDateKey);

        const entityDateDue = (doc as IOtherLiabilityEntity)?.DateDue;

        const dateDue = entityDateDue ? entityDateDue : isValidPath ? options?.storage.getValueByPath(localContextDueDateKey) : null;

        return {
            id: doc.Id,
            values: {
                date: DateType.format(doc.DateAccountingTransaction ?? doc.DateCbaDocument),
                doc: doc.NumberOurs
                    ? getTableIntentLink(doc.NumberOurs, {
                        route: `${getRouteByDocumentType(doc.DocumentTypeCode as DocumentTypeCode)}/${doc.Id}`,
                        context,
                    })
                    : EMPTY_DASH,
                dueDate: dateDue ? DateType.format(dateDue) : EMPTY_DASH,
                docType: doc.DocumentTypeCode
                    ? getEnumDisplayValue(EntityTypeName.DocumentType, doc.DocumentTypeCode)
                    : EMPTY_DASH,
                name: (doc as IInternalDocumentEntity).Explanation ?? doc.Note ?? EMPTY_DASH,
                sum: formatCurrency(doc.Amount, getCompanyCurrency(context)),
            },
        };
    });

    return (
        <TableWrapper data-testid={TestIds.TaxDocumentsTable}>
            <TableWithAutoSizedColumns
                busy={isLoading}
                rows={rows ?? []}
                columns={columns}
                disableSort={true}
                disableVirtualization={true}
            />
        </TableWrapper>
    );
};

interface AccountInfo {
    AccountName: string;
    AccountNumber: string;
    AccountBalance: string;
    ChartOfAccountsId: number;
    AccountId: number;
}

export interface IFYCloseValidationError {
    AccountInfos: AccountInfo[];
    ErrorParameters: TRecordString;
    ErrorCode: string;
    SectionCode: string;
}

const renderAdditionalErrorDataTable = (
    e: IFYCloseValidationError,
    fiscalYear: IFiscalYearEntity,
    context: IAppContext
): ReactElement => {
    const accountInfosSortFn = (a: AccountInfo, b: AccountInfo) => {
        if (a?.AccountNumber < b?.AccountNumber) {
            return -1;
        }
        if (a?.AccountNumber > b?.AccountNumber) {
            return 1;
        }
        return 0;
    };

    if (e.AccountInfos?.some((info) => info.AccountBalance)) {
        const dateRange: IValueInterval = {
            from: formatDateToDateString(fiscalYear?.DateStart),
            to: formatDateToDateString(fiscalYear?.DateEnd),
        };

        return (
            <AdditionalAccountInfoTable>
                <div>{i18next.t("FiscalYearClose:Account")}</div>
                <div>{i18next.t("FiscalYearClose:Balance")}</div>
                {e.AccountInfos.sort(accountInfosSortFn).map((info, i) => {
                    return (
                        <Fragment key={i}>
                            <div>{`${info.AccountNumber} - ${info.AccountName}`}</div>
                            <div>
                                {getDrillDownLink(formatCurrency(info.AccountBalance, getCompanyCurrency(context)), {
                                    route: ROUTE_ACCOUNT_ANALYSIS,
                                    context,
                                    filters: {
                                        [AccountAnalysisProps.accounts]: [info.AccountNumber],
                                        [CommonReportProps.dateRange]: CUSTOM_DATE_RANGE_ID,
                                        [CommonReportProps.dateRangeCustomValue]: dateRange,
                                        [AccountAnalysisProps.closingAccounts]: ClosingAccounts.Show,
                                        [AccountAnalysisProps.sumAmounts]: true,
                                    },
                                })}
                            </div>
                        </Fragment>
                    );
                })}
            </AdditionalAccountInfoTable>
        );
    } else if (e.AccountInfos?.length) {
        return (
            <AccountNamesTableStyled>
                {e.AccountInfos.sort(accountInfosSortFn).map((info) => {
                    return (
                        <AccountNameStyled key={info.AccountNumber}>
                            {getDrillDownLink(`${info.AccountNumber} - ${info.AccountName}`, {
                                route: `${ROUTE_CHARTS_OF_ACCOUNTS}/${info.ChartOfAccountsId}/Accounts/${info.AccountId}`,
                                context,
                            })}
                        </AccountNameStyled>
                    );
                })}
            </AccountNamesTableStyled>
        );
    }
    return null;
};

export const validateStep = async (
    type: FiscalYearCloseSectionTypeCode,
    fy: IFiscalYearEntity,
    context: IAppContext
): Promise<{
    alert: IAlertProps;
    sections?: FiscalYearCloseSectionTypeCode[];
}> => {
    const res = await customFetch(`${STEP_VALIDATION_URL}/${fy.Id}/${type}`);
    const errors: IFYCloseValidationError[] = await res.json();
    if (errors?.length) {
        return {
            alert: {
                status: Status.Error,
                title: i18next.t(`FiscalYearClose:ImpermeableError`, { count: errors.length }),
                subTitle: errors.map((e: IFYCloseValidationError) => {
                    return (
                        <>
                            {i18next.t(`Error:${e.ErrorCode}`, e.ErrorParameters)}
                            {renderAdditionalErrorDataTable(e, fy, context)}
                        </>
                    );
                }),
                action: AlertAction.None,
            },
            sections: errors.map((e) => e.SectionCode) as FiscalYearCloseSectionTypeCode[],
        };
    }

    return {
        alert: null,
    };
};

const sortByDate = (doc1: IDocumentEntity, doc2: IDocumentEntity) => {
    return getUtcDayjs(doc1.DateAccountingTransaction ?? doc1.DateCbaDocument).diff(
        doc2.DateAccountingTransaction ?? doc2.DateCbaDocument
    );
};

export const getTaxStatementDocs = (fyClose: IFiscalYearCloseEntity): IDocumentEntity[] => {
    const { IncomeTaxPaymentOrReturnDocument, IncomeTaxDocument, AdvanceTaxPayments } = fyClose;

    const taxRelationName = i18next.t(
        `FiscalYearClose:TaxStatement.${
            IncomeTaxPaymentOrReturnDocument?.DocumentTypeCode === DocumentTypeCode.OtherReceivable
                ? "IncomeTaxReceivable"
                : "IncomeTaxLiability"
        }`
    );
    const documents = [{ ...IncomeTaxPaymentOrReturnDocument, Note: taxRelationName }, IncomeTaxDocument].filter(
        (doc) => !!doc?.Id
    );

    // relation name for AdvanceTaxPayments should be generic
    const relationName = i18next.t("FiscalYearClose:TaxStatement.IncomeTaxAdvance");
    for (const payment of AdvanceTaxPayments ?? []) {
        if (!!payment.CreatedDocument?.Id) {
            documents.push({ ...payment.CreatedDocument, Note: relationName });
        } else {
            // virtual document (we take the info from AdvanceTaxPayment navigation)
            documents.push({
                ...payment,
                Note: relationName,
                DocumentTypeCode: DocumentTypeCode.OtherLiability,
            });
        }
    }
    return documents;
};

const sortedLegalEntityCategory = [
    LegalEntityCategoryCode.Micro,
    LegalEntityCategoryCode.Small,
    LegalEntityCategoryCode.Medium,
    LegalEntityCategoryCode.Large,
];

export const getLegalEntityCategoryItems = memoize((): ISelectItem[] => {
    return Object.values(sortedLegalEntityCategory).map((val: string) => {
        return {
            id: val,
            label: i18next.t(`FiscalYearClose:TaxStatement.${val}`),
        };
    });
});

export const fycAlertSettings: IWithAlertSettings = { style: { marginBottom: "20px" } };

export const sortedFYCTasks = [
    FiscalYearCloseSectionTypeCode.PayableReceivable,
    FiscalYearCloseSectionTypeCode.Bank,
    FiscalYearCloseSectionTypeCode.CashBox,
    FiscalYearCloseSectionTypeCode.Accrued,
    FiscalYearCloseSectionTypeCode.DeferredPlan,
    FiscalYearCloseSectionTypeCode.Asset,
    FiscalYearCloseSectionTypeCode.ReceivableAdjustments,
    FiscalYearCloseSectionTypeCode.Inventory,
    FiscalYearCloseSectionTypeCode.ForeignCurrencies,
    FiscalYearCloseSectionTypeCode.TaxStatement,
    FiscalYearCloseSectionTypeCode.FinalClosure,
];

export const sortedFYCTasksForCba = [
    FiscalYearCloseSectionTypeCode.ExchangeRates,
    FiscalYearCloseSectionTypeCode.PayableReceivable,
    FiscalYearCloseSectionTypeCode.Bank,
    FiscalYearCloseSectionTypeCode.CashBox,
    FiscalYearCloseSectionTypeCode.Asset,
    FiscalYearCloseSectionTypeCode.TaxStatement,
    FiscalYearCloseSectionTypeCode.FinalClosure,
];

export const getPaymentScreenCommonColumns = (
    commonCols: IColumn[],
    fyNumber: string,
    hasAccounting: boolean,
    t: TFunction
): IColumn[] => {
    const result = [...commonCols];
    if (hasAccounting) {
        result.push({ id: "balance", label: t("FiscalYearClose:AccountingBalance"), textAlign: TextAlign.Right });
    } else {
        result.push({
            id: "cbaBalance",
            label: t("FiscalYearClose:Balance"),
            info: t("FiscalYearClose:CbaBalanceInfo", { number: fyNumber }),
            textAlign: TextAlign.Right,
        });
    }
    return result;
};

/*  IncomeTaxPaymentOrReturnDocument has type Document in metadata, because it is either IOtherReceivableEntity or IOtherLiabilityEntity,
 *   and those don't have any other common parent model. Yet we need DateDue which is not present in Document model, and normal expand would fail */
export async function loadDateDueToLocalContext(
    doc: IOtherReceivableEntity | IOtherLiabilityEntity,
    oData: OData,
    storage: FormStorage
): Promise<void> {
    if (doc?.Id) {
        const docRes = await oData
            .getEntitySetWrapper(getEntitySetByDocumentType(doc.DocumentTypeCode as DocumentTypeCode))
            .query(doc.Id)
            .select(OtherLiabilityEntity.DateDue)
            .fetchData<IOtherReceivableEntity | IOtherLiabilityEntity>();

        const localContextPath = BindingContext.localContext(createPath(LOCAL_CONTEXT_DUE_DATE, doc?.Id?.toString()));
        storage.setValueByPath(localContextPath, docRes.value?.DateDue);
    }
}

